import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/** 열려 있는 이벤트 상세를 가리키는 검색 파라미터. */
const PARAM = "event";

/**
 * 이벤트 상세 모달의 열림 상태 — **주소가 소유한다.**
 *
 * React state 로만 두지 않는 이유는 푸시 알림 때문이다. 푸시는 앱 밖(잠금화면·알림센터)에서
 * 들어오고 브라우저가 할 수 있는 건 "이 주소를 열어라" 뿐이라, 모달이 주소로 표현돼 있지
 * 않으면 **푸시로는 절대 모달을 띄울 수 없다.** 그러면 같은 알람인데 인앱은 모달,
 * 푸시는 현장 이동으로 목적지가 갈린다(`push-sw.js` 의 `alarmUrl` 과 짝을 이룬다).
 *
 * 덤으로 새로고침·링크 공유가 되고, 뒤로가기가 "모달만 닫기"로 자연스럽게 동작한다.
 *
 * 숫자 id 만 받는다 — 상세 조회(`GET /events/:id`)가 숫자 키라서, 문자열 `eventId` 로는
 * 아무것도 못 가져온다. 이상한 값이 붙어 오면 모달을 열지 않고 무시한다.
 */
export function useEventModal() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const raw = params.get(PARAM);
  const eventId = raw != null && /^\d+$/.test(raw) ? Number(raw) : null;

  const openEvent = useCallback(
    (id: number) => {
      const next = new URLSearchParams(params);
      next.set(PARAM, String(id));
      // push 로 쌓아야 뒤로가기가 모달만 닫는다. replace 면 뒤로가기가 화면을 벗어난다.
      setParams(next);
    },
    [params, setParams]
  );

  const closeEvent = useCallback(() => {
    /*
      되돌아갈 히스토리가 있으면 뒤로 간다 — 열 때 push 했으므로 주소가 정확히 원상복구된다.

      푸시·딥링크로 `?event=` 를 달고 **첫 진입**한 경우엔 되돌아갈 곳이 없다. 그때
      `navigate(-1)` 은 앱을 벗어나므로, 파라미터만 지운다(`SiteShellHeader` 의 뒤로가기와
      같은 판별 방식이다).
    */
    if (location.key === "default") {
      const next = new URLSearchParams(params);
      next.delete(PARAM);
      setParams(next, { replace: true });
    } else {
      navigate(-1);
    }
  }, [location.key, navigate, params, setParams]);

  return { eventId, openEvent, closeEvent };
}
